import { submitFormJson } from 'widgets/toolbox/ajax';
import { loadScript } from 'widgets/toolbox/util';

/**
 * @typedef {ReturnType<typeof import('widgets/checkout/BillingForm').default>} BillingForm
 * @typedef {InstanceType<ReturnType<typeof import('widgets/checkout/PaymentAccordion').default>>} paymentAccordion
 */

/**
 * @description AuthorizeNetPaymentMixin implementation
 * @param {Widget} Widget widget for extending
 * @returns {typeof AuthorizeNetPaymentMixin}
 */
export default function (Widget) {
    /**
     * @class AuthorizeNetPaymentMixin
     * @augments Widget
     * @classdesc AuthorizeNetPaymentMixin provides logic for CC processing via Authorize.net.
     * AT first we initialize Accept.js (Accept Suite client side JS library) which allows us to get a nonce.
     * After that we shall send the nonce(opaqueData) which comes from the response to SFCC.
     */
    return class AuthorizeNetPaymentMixin extends Widget {
        prefs() {
            return {
                authorizeClientUrl: '',
                authorizeClientKey: '',
                authorizeApiLoginId: '',
                opaqueDataField: '',
                cardNumberField: '',
                cardCodeField: '',
                expirationDateField: '',
                paymentFields: [],
                ...super.prefs()
            };
        }

        init() {
            // Accept Suite is a client side js library provided by Authorize.net
            // which allows us to get a nonce so customer doesn't send credit card data to SFCC
            const { authorizeClientUrl } = this.prefs();

            if (authorizeClientUrl) {
                loadScript(authorizeClientUrl);
            }

            super.init();
        }

        /**
         * Prepares and sends credit card data to Accept Suite
         * @param {object} cardFields
         */
        dispatchData(cardFields) {
            const {
                authorizeClientKey,
                authorizeApiLoginId,
                cardNumberField,
                cardCodeField,
                expirationDateField
            } = this.prefs();

            if (!authorizeClientKey
                || !authorizeApiLoginId
                || !window.Accept
            ) {
                this.handleTokenError();
                return;
            }

            const data = {
                authData: {
                    clientKey: authorizeClientKey,
                    apiLoginID: authorizeApiLoginId
                },
                cardData: {
                    cardNumber: cardFields[cardNumberField],
                    month: cardFields[expirationDateField].split('/')[0],
                    year: cardFields[expirationDateField].split('/')[1],
                    cardCode: cardFields[cardCodeField]
                }
            };

            this.cardFields = cardFields;

            // @ts-ignore
            window.Accept.dispatchData(data, this.submitToken.bind(this));
        }

        /**
         * Accept Suite callback to send opaqueData (which comes from the Authorize.net response) to SFCC
         * @param {object} response
         * @param {object} response.opaqueData Generic payment data type which is used
         * to process tokenized or encrypted payment information
         */
        submitToken(response) {
            this.handleTokenError(response);

            if (!response.opaqueData) {
                return;
            }

            const {
                paymentFields,
                opaqueDataField
            } = this.prefs();
            const data = {
                ...this.getFormFields(),
                [opaqueDataField]: JSON.stringify(response.opaqueData)
            };

            paymentFields.forEach(field => {
                data[field] = this.cardFields[field];
            });

            delete this.cardFields;

            submitFormJson(this.getFormUrl(), data, 'POST')
                .then(this.onSubmitted.bind(this))
                .finally(this.afterSubmission.bind(this));
        }
    };
}
